import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Public, Checkout } from '../api.js';
import { useAuth } from '../AuthContext.jsx';
import { PageHeader } from '../components/PageHeader.jsx';

export default function Shop() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Public.products()
      .then((r) => setProducts(r.products))
      .catch(() => setProducts([]));
  }, []);

  async function buy(product, variantSku) {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    setBusy(product.id + (variantSku || ''));
    setErr(null);
    try {
      const { url } = await Checkout.create([{ productId: product.id, variantSku, quantity: 1 }]);
      window.location.href = url;
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  }

  const merch = products.filter((p) => p.type === 'merch');
  const tickets = products.filter((p) => p.type === 'ticket');
  const dues = products.filter((p) => p.type === 'dues');

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <PageHeader eyebrow="Shop" title="Kit, tickets, dues." description="Everything that keeps the lights on and the jerseys clean." />
      {!user && (
        <div className="card mb-8 text-sm flex items-center justify-between gap-4 flex-wrap">
          <span className="text-ink-2">
            <Link to="/login" className="font-medium text-brand underline underline-offset-4">Sign in</Link> to purchase.
          </span>
        </div>
      )}
      {err && <p className="text-sm text-danger mb-4">{err}</p>}

      <Section title="Merch" eyebrow="01 · Kit">
        {merch.map((p) => (
          <article key={p.id} className="card flex flex-col">
            {p.imageUrl ? (
              <img src={p.imageUrl} alt={p.name} className="w-full aspect-square object-cover rounded-md mb-5 bg-line-soft" />
            ) : (
              <div className="w-full aspect-square rounded-md mb-5 bg-gradient-to-br from-line-soft to-line flex items-center justify-center">
                <span className="font-display text-5xl text-ink-3">{p.name[0]}</span>
              </div>
            )}
            <h3 className="font-display text-xl">{p.name}</h3>
            {p.description && <p className="text-sm text-ink-2 mt-1.5 line-clamp-2">{p.description}</p>}
            <div className="mt-5 space-y-2 flex-1">
              {p.variants.map((v) => (
                <div key={v.sku} className="flex items-center justify-between py-1.5 border-b border-line last:border-0">
                  <span className="text-sm text-ink-2">
                    {[v.size, v.color].filter(Boolean).join(' · ') || v.sku}
                    {v.stock === 0 && <span className="chip-neutral ml-2">Out</span>}
                  </span>
                  <button
                    className="btn-outline btn-xs"
                    disabled={v.stock === 0 || busy === p.id + v.sku}
                    onClick={() => buy(p, v.sku)}
                  >
                    Buy
                  </button>
                </div>
              ))}
            </div>
          </article>
        ))}
        {!merch.length && <Empty>No merch yet.</Empty>}
      </Section>

      <Section title="Match tickets" eyebrow="02 · Fixtures">
        {tickets.map((p) => {
          const remaining = p.capacity - (p.sold || 0);
          return (
            <article key={p.id} className="card flex flex-col">
              <h3 className="font-display text-xl">{p.name}</h3>
              {p.description && <p className="text-sm text-ink-2 mt-1.5">{p.description}</p>}
              <div className="mt-4 text-xs text-ink-3">
                {remaining} of {p.capacity} remaining
              </div>
              <div className="mt-auto pt-6 flex items-end justify-between">
                <span className="numeric text-3xl text-ink">${(p.priceCents / 100).toFixed(2)}</span>
                <button
                  className="btn-primary btn-sm"
                  disabled={remaining <= 0 || busy === p.id}
                  onClick={() => buy(p)}
                >
                  {remaining <= 0 ? 'Sold out' : 'Buy ticket'}
                </button>
              </div>
            </article>
          );
        })}
        {!tickets.length && <Empty>No tickets on sale.</Empty>}
      </Section>

      <Section title="Membership dues" eyebrow="03 · Support">
        {dues.map((p) => (
          <article key={p.id} className="card flex flex-col">
            <h3 className="font-display text-xl">{p.name}</h3>
            {p.description && <p className="text-sm text-ink-2 mt-1.5">{p.description}</p>}
            <div className="mt-auto pt-6">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="numeric text-4xl text-ink">${(p.priceCents / 100).toFixed(2)}</span>
                <span className="text-xs text-ink-3">/ {p.intervalMonths}mo</span>
              </div>
              <button className="btn-primary w-full" disabled={busy === p.id} onClick={() => buy(p)}>
                Subscribe
              </button>
            </div>
          </article>
        ))}
        {!dues.length && <Empty>No membership tiers configured.</Empty>}
      </Section>
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <section className="mb-14 md:mb-20">
      <div className="flex items-end justify-between mb-6 pb-3 border-b border-line">
        <div>
          <div className="eyebrow mb-1">{eyebrow}</div>
          <h2 className="font-display text-3xl">{title}</h2>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return <div className="col-span-full text-center py-12 text-ink-3 text-sm italic font-display">{children}</div>;
}
